import { z } from 'zod';
import { ProgressStateSchema } from './progress.js';

/**
 * Comptes joueurs (optionnels) : un pseudonyme et un mot de passe, rien
 * d'autre — pas d'e-mail, pas de récupération. Le compte ne sert qu'à
 * retrouver sa progression d'un appareil à l'autre ; le jeu reste entièrement
 * jouable sans.
 *
 * Comme toutes les frontières (spec §6), requêtes ET réponses passent par zod
 * des deux côtés.
 */

export const PSEUDO_MIN_LENGTH = 3;
export const PSEUDO_MAX_LENGTH = 20;
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

/** Lettres (accents compris), chiffres, `.`, `_` et `-` — rien d'autre. */
export const PseudoSchema = z
  .string()
  .trim()
  .min(PSEUDO_MIN_LENGTH)
  .max(PSEUDO_MAX_LENGTH)
  .regex(/^[\p{L}\p{N}._-]+$/u);

export const PasswordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);

/** Corps de `POST /api/account/register` et `POST /api/account/login`. */
export const AccountCredentialsSchema = z.object({
  pseudo: PseudoSchema,
  password: PasswordSchema,
});

/** Réponse d'une inscription ou d'une connexion réussie. */
export const AccountSessionResponseSchema = z.object({
  /** Jeton de session opaque, à présenter en `Authorization: Bearer …`. */
  token: z.string().min(1),
  pseudo: z.string(),
  /** Progression déjà connue du serveur pour ce compte. */
  progress: ProgressStateSchema,
});

/** Réponse de `GET /api/account/me`. */
export const AccountMeResponseSchema = z.object({
  pseudo: z.string(),
});

/** Corps de `PUT /api/account/progress` et réponse des deux routes progress. */
export const AccountProgressBodySchema = z.object({
  progress: ProgressStateSchema,
});

export type AccountCredentials = z.infer<typeof AccountCredentialsSchema>;
export type AccountSessionResponse = z.infer<typeof AccountSessionResponseSchema>;
export type AccountMeResponse = z.infer<typeof AccountMeResponseSchema>;
export type AccountProgressBody = z.infer<typeof AccountProgressBodySchema>;
